import React, {
    Dispatch, SetStateAction, createContext, useContext, useEffect, useState,
} from 'react';
import { PageSpecificKeys } from 'helpers/localStorage';
import { IUsesActualsExpandedCategories, IUsesExpandedCategories } from 'views/ProFormaTable/types';
import { USES_EXPANDED_CATEGORIES } from 'views/ProFormaTable/constants';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';

export interface IUsesTableContext {
    expandedCategories: IUsesExpandedCategories;
    setExpandedCategories: Dispatch<SetStateAction<IUsesExpandedCategories>>;
    updateExpandedCategories: (highLevelId: number | null, subcategoryId: number | null) => void;
    hideAllCategories: () => void;
    showAllCategories: (updatedCategories: IUsesExpandedCategories) => void;
    expandedActualsCategories: IUsesActualsExpandedCategories;
    setExpandedActualsCategories: Dispatch<SetStateAction<IUsesActualsExpandedCategories>>;
    updateExpandedActualsCategories: (highLevel: number | null, subcategory: number | null) => void;
    hideAllActualsCategories: () => void;
    showAllActualsCategories: (updatedCategories: IUsesActualsExpandedCategories) => void;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    forecastMetric: string;
    selectedScenario: number | null;
    isEarlyPlanningProject: boolean;
}

export const defaultValues: IUsesTableContext = {
    expandedCategories: { highLevels: [], subcategories: [] },
    setExpandedCategories: () => { },
    updateExpandedCategories: () => { },
    hideAllCategories: () => { },
    showAllCategories: () => { },
    expandedActualsCategories: { highLevels: [], subcategories: [] },
    setExpandedActualsCategories: () => { },
    updateExpandedActualsCategories: () => { },
    hideAllActualsCategories: () => { },
    showAllActualsCategories: () => { },
    navigateToSection: () => { },
    forecastMetric: '',
    selectedScenario: null,
    isEarlyPlanningProject: false,
};

const UsesTableContext = createContext<IUsesTableContext>(defaultValues);

interface IUsesTableActualsProviderProps {
    children: React.ReactNode;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    forecastMetric: string;
    selectedScenario: number | null;
    isEarlyPlanningProject: boolean;
}
const UsesTableActualsProvider = ({
    children, navigateToSection, forecastMetric, selectedScenario, isEarlyPlanningProject,
}: IUsesTableActualsProviderProps) => {
    const { proForma } = useProFormaSocketContext();
    const proFormaPageLocalStorage = JSON.parse(localStorage.getItem(PageSpecificKeys.ProFormaPage) ?? '{}');

    const [expandedCategories, setExpandedCategories] = useState<IUsesExpandedCategories>({ highLevels: [], subcategories: [] });

    const updateExpandedCategories = (highLevelId: number | null, subcategoryId: number | null) => {
        if (highLevelId) {
            setExpandedCategories(prev => ({
                ...prev,
                highLevels: prev.highLevels.includes(highLevelId) ? prev.highLevels.filter(id => id !== highLevelId) : [...prev.highLevels, highLevelId],
            }));
        }

        if (subcategoryId) {
            setExpandedCategories(prev => ({
                ...prev,
                subcategories: prev.subcategories.includes(subcategoryId)
                    ? prev.subcategories.filter(id => id !== subcategoryId)
                    : [...prev.subcategories, subcategoryId],
            }));
        }
    };

    const hideAllCategories = () => {
        setExpandedCategories({ highLevels: [], subcategories: [] });
    };

    const showAllCategories = (updatedCategories: IUsesExpandedCategories) => {
        setExpandedCategories(updatedCategories);
    };

    const [expandedActualsCategories, setExpandedActualsCategories] = useState<IUsesActualsExpandedCategories>({ highLevels: [], subcategories: [] });
    const updateExpandedActualsCategories = (highLevel: number | null, subcategory: number | null) => {
        if (highLevel) {
            setExpandedActualsCategories(prev => ({
                ...prev,
                highLevels: prev.highLevels.includes(highLevel) ? prev.highLevels.filter(hl => hl !== highLevel) : [...prev.highLevels, highLevel],
            }));
        }
        if (subcategory) {
            setExpandedActualsCategories(prev => ({
                ...prev,
                subcategories: prev.subcategories.includes(subcategory)
                    ? prev.subcategories.filter(sc => sc !== subcategory)
                    : [...prev.subcategories, subcategory],
            }));
        }
    };
    const showAllActualsCategories = (updatedCategories: IUsesActualsExpandedCategories) => {
        setExpandedActualsCategories(updatedCategories);
    };
    const hideAllActualsCategories = () => {
        setExpandedActualsCategories({ highLevels: [], subcategories: [] });
    };

    useEffect(() => {
        if (!proForma) return;

        const proFormaPageLocalStorage = JSON.parse(localStorage.getItem(PageSpecificKeys.ProFormaPage) ?? '{}');
        const updatedProFormaPageLocalStorage = {
            ...proFormaPageLocalStorage,
            [proForma.id]: {
                ...proFormaPageLocalStorage[proForma.id],
                [USES_EXPANDED_CATEGORIES]: expandedCategories,
            },
        };

        localStorage.setItem(PageSpecificKeys.ProFormaPage, JSON.stringify(updatedProFormaPageLocalStorage));
    }, [expandedCategories, proForma]);

    useEffect(() => {
        if (proFormaPageLocalStorage) {
            const currentProFormaLocalStorage = proForma && proForma.id in proFormaPageLocalStorage
                ? proFormaPageLocalStorage[proForma.id]
                : {};

            if (expandedCategories.highLevels.length === 0 && expandedCategories.subcategories.length === 0) {
                const savedExpandedCategories = USES_EXPANDED_CATEGORIES in currentProFormaLocalStorage
                    ? currentProFormaLocalStorage[USES_EXPANDED_CATEGORIES]
                    : { highLevels: [], subcategories: [] };
                setExpandedCategories(savedExpandedCategories as IUsesExpandedCategories);
            }
        }
    }, []);

    return (
        <UsesTableContext.Provider
            value={{
                expandedCategories,
                setExpandedCategories,
                updateExpandedCategories,
                hideAllCategories,
                showAllCategories,
                expandedActualsCategories,
                setExpandedActualsCategories,
                updateExpandedActualsCategories,
                hideAllActualsCategories,
                showAllActualsCategories,
                navigateToSection,
                forecastMetric,
                selectedScenario,
                isEarlyPlanningProject,
            }}
        >
            {children}
        </UsesTableContext.Provider>
    );
};

const useUsesActualsTableContext = () => useContext(UsesTableContext);

export { UsesTableActualsProvider, useUsesActualsTableContext };
